import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageResponse } from '@/api';
import type { MessageData } from '@/lib/messages';
import {
  DecryptionError,
  decryptAndCacheMessage,
  isNip44Supported,
  isNostrExtensionAvailable,
  NostrExtensionError,
} from '@/lib/nostr-decrypt';
import classes from './Inbox.module.css';

export interface NostrDecryptCardProps {
  message: MessageResponse;
  onDecrypted: (data: MessageData) => void;
}

/** The locked state of an end-to-end encrypted message; decryption runs in the browser extension. */
export function NostrDecryptCard({ message, onDecrypted }: NostrDecryptCardProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decrypt = async () => {
    setError(null);
    // Extensions inject window.nostr after load, so check at click time.
    if (!isNostrExtensionAvailable()) {
      setError(t('inbox.nostrNoExtension'));
      return;
    }
    if (!isNip44Supported()) {
      setError(t('inbox.nostrNoNip44'));
      return;
    }
    const ciphertext = message.data_encrypted || message.data;
    if (!message.encryption_pubkey || !ciphertext) {
      setError(t('inbox.nostrMissingKey'));
      return;
    }
    setBusy(true);
    try {
      onDecrypted(await decryptAndCacheMessage(message.id, ciphertext, message.encryption_pubkey));
    } catch (err) {
      const reason =
        err instanceof NostrExtensionError || err instanceof DecryptionError
          ? err.message
          : String(err);
      setError(t('inbox.nostrFailed', { reason }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={classes.nostr}>
      <strong>{t('inbox.nostrTitle')}</strong>
      <span className={classes.sub}>{t('inbox.nostrText')}</span>
      <Button size="xs" onClick={() => void decrypt()} loading={busy}>
        {t('inbox.nostrOpen')}
      </Button>
      {error && (
        <p className={classes.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
